/**
 * Dialogs de ação do CHAT: Transferir (atendente OU departamento, v8),
 * Finalizar/Cancelar (motivo obrigatório + apontamento opcional → POST
 * archive, spec 013) e vínculos de ticket/cliente (PUT).
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CHAT_CLOSE_NOTE_MAX, type ChatDetail } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input, Label, Select, Textarea } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, Spinner } from '@/components/ui/misc';
import { useAuthUser } from '@/auth/use-auth';
import { desksListQuery } from '@/features/tickets/tickets-queries';
import { teamListQuery } from '@/features/team/team-queries';
import { ClientPicker, type ClientRef } from '@/features/tickets/ClientPicker';
import { useArchiveChat, useTransferChat, useUpdateChat } from './chats-mutations';
import { closeReasonsOptions } from './chats-queries';
import { appointmentErrorMessage, errorMessage } from './chats-format';
import { presenceByUser, teamPresenceQuery } from '@/features/presence/presence-queries';
import { presenceLabel } from '@/features/presence/presence-chip';

type TransferMode = 'user' | 'desk';

export function TransferChatDialog({
  chat,
  onClose,
  onTransferred,
}: {
  chat: ChatDetail;
  onClose: () => void;
  /** transferência confirmada pela API — ex.: toast no chamador */
  onTransferred?: () => void;
}) {
  const me = useAuthUser();
  const transfer = useTransferChat();
  // só a equipe deste tenant, ativa (pedido da PoolTrack, 05/10) — a API
  // recusa transferir para fora dela
  const usersQ = useQuery(teamListQuery());
  const users = (usersQ.data?.items ?? []).filter(
    (u) => u.id !== chat.responsible?.id && u.id !== me?.id
  );
  // spec 015: a lista mostra o status de cada um e quem está em PAUSA não é
  // escolhível (a API recusaria com 409 attendant_paused). Atualiza sozinha
  // pelo evento presence.changed enquanto o diálogo está aberto.
  const presenceQ = useQuery(teamPresenceQuery());
  const presence = presenceByUser(presenceQ.data);
  // Transferir para departamento (v8): o chat volta à Entrada da mesa destino.
  const desksQ = useQuery(desksListQuery());
  const desks = (desksQ.data ?? []).filter((d) => d.active && d.id !== chat.desk?.id);

  const [mode, setMode] = useState<TransferMode>('user');
  const [userId, setUserId] = useState('');
  const [deskId, setDeskId] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Config do destino por modo — Select, validação e corpo do POST derivam
  // daqui (um único bloco de render para os dois modos).
  const target =
    mode === 'user'
      ? {
          id: 'tc-user',
          label: 'Atendente',
          missing: 'Selecione o atendente de destino.',
          pending: usersQ.isPending,
          options: users.map((u) => {
            const p = presence.get(u.id);
            return {
              id: u.id,
              name: `${u.name} — ${presenceLabel(p)}`,
              disabled: p?.status === 'paused',
            };
          }),
          value: userId,
          set: setUserId,
        }
      : {
          id: 'tc-desk',
          label: 'Departamento',
          missing: 'Selecione o departamento de destino.',
          pending: desksQ.isPending,
          options: desks.map((d) => ({ id: d.id, name: d.displayName, disabled: false })),
          value: deskId,
          set: setDeskId,
        };

  const submit = () => {
    setError(null);
    if (target.value === '') {
      setError(target.missing);
      return;
    }
    transfer.mutate(
      mode === 'user'
        ? { chatId: chat.id, userId: Number(target.value) }
        : { chatId: chat.id, deskId: Number(target.value) },
      {
        onSuccess: () => {
          onTransferred?.();
          onClose();
        },
        onError: (e) => setError(errorMessage(e)),
      }
    );
  };

  return (
    <Dialog open onClose={onClose} title="Transferir conversa">
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Tabs value={mode} onValueChange={(v) => setMode(v as TransferMode)}>
          <TabsList className="h-8 w-full">
            <TabsTrigger value="user" className="text-xs">
              Transferir para atendente
            </TabsTrigger>
            <TabsTrigger value="desk" className="text-xs">
              Transferir para departamento
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <p className="text-sm text-muted-foreground">
          {mode === 'user' ? (
            <>
              A conversa com{' '}
              <span className="font-medium text-foreground">{chat.requestorName}</span> passará a
              ser atendida pelo usuário selecionado.
            </>
          ) : (
            <>
              A conversa com{' '}
              <span className="font-medium text-foreground">{chat.requestorName}</span> voltará
              para a Entrada do departamento selecionado, sem responsável — qualquer atendente da
              mesa poderá assumi-la.
            </>
          )}
        </p>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor={target.id} className="text-[13px] font-semibold">
            {target.label}
            <span className="-ml-1 text-destructive">*</span>
          </Label>
          <Select
            id={target.id}
            className="h-8"
            value={target.value}
            onChange={(e) => target.set(e.target.value)}
            disabled={target.pending}
          >
            <option value="">Selecione…</option>
            {target.options.map((o) => (
              <option key={o.id} value={o.id} disabled={o.disabled}>
                {o.name}
              </option>
            ))}
          </Select>
        </div>

        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}

        <div className="mt-1 flex justify-end gap-2 border-t pt-3">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={transfer.isPending}>
            {transfer.isPending ? <Spinner className="size-4 border-primary-foreground" /> : null}
            Transferir
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/**
 * Finalizar (canceled=false) ou Cancelar (canceled=true) — spec 013: o MOTIVO
 * é obrigatório (lista ativa do tenant), a observação é livre, e com ticket
 * vinculado aparece "Gerar apontamento com o histórico" (marcado por padrão).
 * Se o apontamento não sair, a conversa já está encerrada: o diálogo troca
 * para um aviso com o porquê, em vez de fechar calado.
 */
export function ArchiveChatDialog({
  chat,
  canceled,
  onClose,
}: {
  chat: ChatDetail;
  canceled: boolean;
  onClose: () => void;
}) {
  const archive = useArchiveChat();
  const reasonsQ = useQuery(closeReasonsOptions({ activeOnly: true }));
  const reasons = reasonsQ.data ?? [];
  const [reasonId, setReasonId] = useState('');
  const [note, setNote] = useState('');
  const hasTicket = chat.ticketNumber !== null;
  const [appointment, setAppointment] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // encerrou, mas o apontamento pedido não saiu: mostra o porquê
  const [appointmentWarning, setAppointmentWarning] = useState<string | null>(null);

  const submit = () => {
    setError(null);
    if (reasonId === '') {
      setError('Escolha o motivo do encerramento.');
      return;
    }
    archive.mutate(
      {
        chatId: chat.id,
        canceled,
        reasonId: Number(reasonId),
        note: note.trim() || null,
        appointment: hasTicket && appointment,
      },
      {
        onSuccess: (result) => {
          if (result.appointmentError) {
            setAppointmentWarning(appointmentErrorMessage(result.appointmentError));
            return;
          }
          onClose();
        },
        onError: (e) => setError(errorMessage(e)),
      }
    );
  };

  const title = canceled ? 'Cancelar conversa' : 'Finalizar conversa';

  if (appointmentWarning !== null) {
    return (
      <Dialog open onClose={onClose} title={title}>
        <div className="flex flex-col gap-3">
          <p className="text-sm">
            Conversa encerrada. O apontamento no ticket #{chat.ticketNumber}{' '}
            <span className="font-semibold">não foi gerado</span>: {appointmentWarning}
          </p>
          <p className="text-sm text-muted-foreground">
            Baixe a conversa pelo menu Ações se precisar lançar o histórico à mão.
          </p>
          <div className="mt-1 flex justify-end border-t pt-3">
            <Button type="button" size="sm" onClick={onClose}>
              Entendi
            </Button>
          </div>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={onClose} title={title}>
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <p className="text-sm text-muted-foreground">
          {canceled
            ? 'A conversa será encerrada como cancelada e sairá da fila de atendimento.'
            : 'A conversa será encerrada e o solicitante poderá avaliar o atendimento.'}
        </p>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="ac-reason" className="text-[13px] font-semibold">
            Motivo<span className="-ml-1 text-destructive">*</span>
          </Label>
          <Select
            id="ac-reason"
            className="h-8"
            value={reasonId}
            onChange={(e) => setReasonId(e.target.value)}
            disabled={reasonsQ.isPending}
          >
            <option value="">{reasonsQ.isPending ? 'Carregando…' : 'Selecione…'}</option>
            {reasons.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </Select>
          {!reasonsQ.isPending && reasons.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              Nenhum motivo ativo — peça ao administrador para configurar em Configurações → Chat.
            </p>
          ) : null}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="ac-note" className="text-[13px] font-semibold">
            Observação
          </Label>
          <Textarea
            id="ac-note"
            rows={3}
            maxLength={CHAT_CLOSE_NOTE_MAX}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Opcional — fica só para a equipe"
          />
        </div>

        {hasTicket ? (
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={appointment}
              onCheckedChange={(v) => setAppointment(v === true)}
              aria-label="Gerar apontamento com o histórico"
            />
            Gerar apontamento com o histórico no ticket #{chat.ticketNumber}
          </label>
        ) : null}

        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}

        <div className="mt-1 flex justify-end gap-2 border-t pt-3">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Voltar
          </Button>
          <Button
            type="submit"
            size="sm"
            variant={canceled ? 'destructive' : 'default'}
            disabled={archive.isPending}
          >
            {archive.isPending ? <Spinner className="size-4 border-primary-foreground" /> : null}
            {title}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** Vincula um ticket EXISTENTE pelo número (PUT { ticketNumber }). */
export function LinkTicketDialog({ chat, onClose }: { chat: ChatDetail; onClose: () => void }) {
  const update = useUpdateChat(chat.id);
  const [ticketNumber, setTicketNumber] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    setError(null);
    const value = ticketNumber.trim().replace(/^#/, '');
    if (!/^\d+$/.test(value)) {
      setError('Informe o número do ticket.');
      return;
    }
    update.mutate(
      { ticketNumber: Number(value) },
      { onSuccess: onClose, onError: (e) => setError(errorMessage(e)) }
    );
  };

  return (
    <Dialog open onClose={onClose} title="Vincular ticket">
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="lt-number" className="text-[13px] font-semibold">
            Número do ticket<span className="-ml-1 text-destructive">*</span>
          </Label>
          <Input
            id="lt-number"
            className="h-8"
            value={ticketNumber}
            onChange={(e) => setTicketNumber(e.target.value)}
            placeholder="Ex.: 1234"
            inputMode="numeric"
            autoFocus
          />
          <p className="text-xs text-muted-foreground">
            Informe o número de um ticket existente para associar a esta conversa.
          </p>
        </div>

        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}

        <div className="mt-1 flex justify-end gap-2 border-t pt-3">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={update.isPending}>
            {update.isPending ? <Spinner className="size-4 border-primary-foreground" /> : null}
            Vincular
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** Vincula/troca/remove o cliente da conversa (PUT { clientId }). */
export function LinkClientDialog({ chat, onClose }: { chat: ChatDetail; onClose: () => void }) {
  const update = useUpdateChat(chat.id);
  const [error, setError] = useState<string | null>(null);

  const save = (client: ClientRef | null) => {
    if (!client) return;
    setError(null);
    update.mutate(
      { clientId: client.id },
      { onSuccess: onClose, onError: (e) => setError(errorMessage(e)) }
    );
  };

  const unlink = () => {
    setError(null);
    update.mutate(
      { clientId: null },
      { onSuccess: onClose, onError: (e) => setError(errorMessage(e)) }
    );
  };

  return (
    <Dialog open onClose={onClose} title="Vincular cliente">
      <div className="flex flex-col gap-3">
        <ClientPicker selected={null} onSelect={save} />

        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}

        <div className="mt-1 flex justify-between gap-2 border-t pt-3">
          {chat.client ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={unlink}
              disabled={update.isPending}
            >
              Remover vínculo
            </Button>
          ) : (
            <span />
          )}
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Fechar
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
